import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getHybridRates, refreshRatesWithAI } from '../services/rates/hybrid-rate-service';
import { useState, useCallback } from 'react';

export function useHybridRates(enableAI: boolean = true) {
  const queryClient = useQueryClient();
  const [lastAIRefresh, setLastAIRefresh] = useState<Date | null>(null);

  const query = useQuery({
    queryKey: ['hybridRates', enableAI],
    queryFn: () => getHybridRates(enableAI),
    refetchInterval: 60000, // Refetch every minute for live data
    staleTime: 30000, // Consider data stale after 30 seconds
    gcTime: 300000, // Garbage collect after 5 minutes
    retry: 2, // Reduced retries for faster failure recovery
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchIntervalInBackground: true,
  });

  const forceRefreshWithAI = useCallback(async () => {
    setLastAIRefresh(new Date());
    const result = await refreshRatesWithAI();
    queryClient.setQueryData(['hybridRates', enableAI], result);
    return result;
  }, [queryClient, enableAI]);

  return {
    ...query,
    officialRates: query.data?.official || [],
    parallelRates: query.data?.parallel || [],
    source: query.data?.source || 'unknown',
    aiValidated: query.data?.aiValidated || false,
    lastUpdated: query.data?.lastUpdated,
    lastAIRefresh,
    forceRefreshWithAI
  };
}

export function useOfficialRatesHybrid(enableAI: boolean = true) {
  const { officialRates, isLoading, isError, refetch, source, aiValidated, forceRefreshWithAI } = useHybridRates(enableAI);

  return {
    data: officialRates,
    isLoading,
    isError,
    refetch,
    source,
    aiValidated,
    forceRefreshWithAI
  };
}

export function useParallelRatesHybrid(enableAI: boolean = true) {
  const { parallelRates, isLoading, isError, refetch, source, aiValidated, forceRefreshWithAI } = useHybridRates(enableAI);

  return {
    data: parallelRates,
    isLoading,
    isError,
    refetch,
    source,
    aiValidated,
    forceRefreshWithAI
  };
}
